import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Classification, Contract, Lane, Regulation, Spot } from 'src/app/core/models';
import { ClassificationService, LaneService, RegulationService, SpotService } from 'src/app/core/services';

export interface InfraliveFilters {
  regulations: number[];
  spots: number[];
  lanes: number[];
  classification: number[];
  refreshDelay: Array<{ id: number; code: string; description: string }>;
  registerAmount: number;
  enableNotPlate: boolean;
}

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.sass']
})
export class FiltersComponent implements OnInit, OnDestroy {

  @Input() contract: Contract;
  @Output() filters = new EventEmitter<InfraliveFilters>();

  public loading = false;
  public contractId: string;
  public filterForm: FormGroup;
  public regulations: Regulation[] = [];
  public spots: Spot[] = [];
  public lanes: Lane[] = [];
  public allLanes: Lane[] = [];
  public classifications: Classification[] = [];
  public refreshDelay = [
    { id: 1, code: '1000', description: '1 Segundo' },
    { id: 2, code: '5000', description: '5 Segundos' },
    { id: 3, code: '10000', description: '10 Segundos' },
    { id: 4, code: '15000', description: '15 Segundos' },
    { id: 5, code: '20000', description: '20 Segundos' },
    { id: 6, code: '30000', description: '30 Segundos' },
    { id: 6, code: '45000', description: '45 Segundos' },
    { id: 7, code: '60000', description: '60 Segundos' },
  ];
  public registerAmount = [
    { id: 1, code: 10, description: '10 Registros' },
    { id: 2, code: 30, description: '30 Registros' },
    { id: 3, code: 40, description: '40 Registros' },
    { id: 4, code: 60, description: '60 Registros' },
    { id: 5, code: 70, description: '70 Registros' },
    { id: 6, code: 90, description: '90 Registros' },
    { id: 6, code: 120, description: '120 Registros' },
  ];

  private spotsSubscription: Subscription;

  constructor(
    private formBuilder: FormBuilder,
    private regulationService: RegulationService,
    private spotService: SpotService,
    private laneService: LaneService,
    private classificationService: ClassificationService,
    private activateRoute: ActivatedRoute
  ) { }

  async ngOnInit() {
    this.loading = true;
    this.contractId = this.activateRoute.snapshot.params.contractId;
    this.createForm();
    await this.getRegulations();
    await this.getSpots();
    await this.getLanes();
    await this.getClassifications();
    this.filterLanesBySpots(this.filterForm.get('spots').value);
    this.spotsSubscription = this.filterForm.get('spots').valueChanges.subscribe(
      (selectedSpotCodes: string[]) => this.filterLanesBySpots(selectedSpotCodes)
    );
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.spotsSubscription?.unsubscribe();
  }

  filterLanesBySpots(selectedSpotCodes: string[]) {
    if (!selectedSpotCodes || selectedSpotCodes.length === 0) {
      this.lanes = [...this.allLanes];
    } else {
      const selectedSpotIds = this.spots
        .filter(s => selectedSpotCodes.includes(s.code))
        .map(s => s.id);
      this.lanes = this.allLanes.filter(l => selectedSpotIds.includes(l.spotId));

      const validLaneCodes = this.lanes.map(l => l.code);
      const currentLanes: string[] = this.filterForm.get('lanes').value || [];
      const filtered = currentLanes.filter(code => validLaneCodes.includes(code));
      if (filtered.length !== currentLanes.length) {
        this.filterForm.get('lanes').patchValue(filtered);
      }
    }
  }

  createForm() {
    const filters = localStorage.getItem('infraliveFilters');
    let filtersData = null;
    if (filters !== null) {
      filtersData = JSON.parse(filters);
    }
    this.filterForm = this.formBuilder.group({
      regulations: [filtersData?.regulations || []],
      spots: [filtersData?.spots || []],
      lanes: [filtersData?.lanes || []],
      classification: [filtersData?.classification || []],
      refreshDelay: [filtersData?.refreshDelay || []],
      registerAmount: [filtersData?.registerAmount || 10],
      enableNotPlate: [true]
    });
  }

  resetFilters() {
    this.filterForm.get('regulations').patchValue([]);
    this.filterForm.get('spots').patchValue([]);
    this.filterForm.get('lanes').patchValue([]);
    this.filterForm.get('classification').patchValue([]);
    this.filterForm.get('refreshDelay').patchValue([]);
    this.filterForm.get('registerAmount').patchValue(10);
    this.filterForm.get('enableNotPlate').patchValue(true);
    localStorage.setItem('infraliveFilters', JSON.stringify(this.filterForm.value));
    this.filters.emit(this.filterForm.value);
  }

  applyFilters() {
    localStorage.setItem('infraliveFilters', JSON.stringify(this.filterForm.value));
    this.filters.emit(this.filterForm.value);
  }

  async getRegulations() {
    const response = await this.regulationService.list({
      contractId: this.contractId,
      limit: '1000',
      'id[in]': `[${this.contract?.ContractConfigColors.map(a => a.regulationId) || ''}]`
    });
    this.regulations = response.result;
    this.regulations.map(regulation => {
      regulation.prettyNames = `${regulation.code} - ${regulation.description}`;
    });
  }

  async getSpots() {
    const response = await this.spotService.list({ contractId: this.contractId });
    this.spots = response.result;
    this.spots.map(spot => {
      spot.prettyNames = `${spot.code} - ${spot.description}`;
    });
  }

  async getLanes() {
    const response = await this.laneService.list({ contractId: this.contractId });
    this.allLanes = response.result;
    this.lanes = [...this.allLanes];
  }

  async getClassifications() {
    const response = await this.classificationService.list({ regionId: this.contract.regionId || '1' });
    this.classifications = response.result;
  }

}
